import { useId } from 'react';

/**
 * Zeit-Fenster-Picker (HH:mm - HH:mm).
 *
 * - Nutzt zwei native <input type="time"> - das gibt uns OS-Picker auf
 *   Mobile/Touch und keyboard input + Locale-Format auf Desktop.
 * - Validierung via API-Spec-Pattern `^([01][0-9]|2[0-3]):[0-5][0-9]$` -
 *   das deckt der native Picker fast komplett ab; zusätzlich onBlur-Check
 *   wäre over-engineered für Single-User-Tool.
 */

interface TimeRangeProps {
  label: string;
  /** HH:mm oder null. */
  start: string | null;
  end: string | null;
  onChange: (next: { start: string | null; end: string | null }) => void;
  hint?: string;
  disabled?: boolean;
  className?: string;
}

export function TimeRange({
  label,
  start,
  end,
  onChange,
  hint,
  disabled = false,
  className,
}: TimeRangeProps): JSX.Element {
  const idPrefix = useId();
  const startId = `${idPrefix}-start`;
  const endId = `${idPrefix}-end`;
  const hintId = hint ? `${idPrefix}-hint` : undefined;

  const update = (field: 'start' | 'end', raw: string): void => {
    const value = raw === '' ? null : raw;
    if (field === 'start') onChange({ start: value, end });
    else onChange({ start, end: value });
  };

  return (
    <div className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      <span className="text-sm font-medium text-text">{label}</span>
      <div className="flex items-center gap-2">
        <label htmlFor={startId} className="sr-only">
          Beginn
        </label>
        <input
          id={startId}
          type="time"
          value={start ?? ''}
          disabled={disabled}
          onChange={(event) => update('start', event.target.value)}
          aria-describedby={hintId}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span aria-hidden="true" className="text-muted">
          –
        </span>
        <label htmlFor={endId} className="sr-only">
          Ende
        </label>
        <input
          id={endId}
          type="time"
          value={end ?? ''}
          disabled={disabled}
          onChange={(event) => update('end', event.target.value)}
          aria-describedby={hintId}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
      {hint && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
